import { logWarn } from 'saddlebag-logger';

import { getConsentCookieName } from './cmp-state';
import { getKetchCookieValue } from './ketch-cookie-utils';

import type { KetchConsentData } from './ketch-types';

/**
 * Module-level state for session-only fallback consent.
 *
 * When Ketch CMP fails to load and no previous consent exists,
 * we apply an in-memory "reject-all" fallback for the current session only.
 * This is NOT persisted to cookie - on next page load, Ketch will attempt to load again.
 */
let fallbackActive = false;

/**
 * Default timeout for Ketch SDK load (in milliseconds).
 * Can be overridden via loadCMPSDK config.
 */
export const DEFAULT_SDK_TIMEOUT_MS = 10000;

/**
 * Default timeout for Ketch API calls (getFullConfig, etc.) in milliseconds.
 * If the callback is not invoked within this period, the call resolves with a default value.
 */
export const DEFAULT_KETCH_API_TIMEOUT_MS = 5000;

/**
 * Sentinel jurisdiction used for fallback consent, where the real
 * jurisdiction can't be determined (Ketch SDK failed to load).
 */
export const UNKNOWN_JURISDICTION = 'unknown';

/**
 * Fallback consent data representing "Accept essential only" / reject-all state.
 * Used when Ketch CMP fails to load and no previous consent exists.
 */
const FALLBACK_CONSENT_DATA: KetchConsentData = {
  purposes: {},
  jurisdiction: UNKNOWN_JURISDICTION,
};

/**
 * Checks if fallback consent should be applied.
 *
 * Fallback is applied when:
 * 1. Ketch CMP fails to load (onError, timeout, or window.ketch unavailable)
 * 2. No previous valid consent exists in the _swb_consent_ cookie
 *
 * If previous consent exists, we do NOT apply fallback.
 *
 * @param cookieString - Optional cookie string for server-side use
 * @returns true if fallback should be applied, false otherwise
 */
export function shouldApplyFallback(cookieString?: string | null): boolean {
  return !getKetchCookieValue(getConsentCookieName(), cookieString);
}

/**
 * Applies the fallback consent state for the current session.
 *
 * This sets an in-memory flag that causes getConsent() to return a reject-all
 * state for the remainder of this session. The fallback is NOT persisted to
 * cookie - on next page load, Ketch will attempt to load again.
 *
 * Only applies when no previous consent exists.
 *
 * @param cookieString - Optional cookie string for server-side use
 * @returns true if fallback was applied, false if skipped (consent already exists)
 */
export function applyFallbackConsent(cookieString?: string | null): boolean {
  if (!shouldApplyFallback(cookieString)) {
    logWarn({
      message:
        'Ketch CMP load failed but previous consent exists - fallback not applied',
      component: 'saddlebag-tracking-preferences',
    });
    return false;
  }

  fallbackActive = true;

  logWarn({
    message:
      'Ketch CMP load failed - applying session-only reject-all fallback',
    component: 'saddlebag-tracking-preferences',
  });

  return true;
}

/**
 * Checks if fallback consent is currently active for this session.
 *
 * @returns true if fallback consent is active, false otherwise
 */
export function isFallbackActive(): boolean {
  return fallbackActive;
}

/**
 * Gets the fallback consent data (reject-all state).
 *
 * This returns empty purposes which means all non-essential tracking is rejected.
 * Only call this when isFallbackActive() returns true.
 *
 * @returns Fallback consent data with empty purposes (reject-all)
 */
export function getFallbackConsent(): KetchConsentData {
  return FALLBACK_CONSENT_DATA;
}

/**
 * Resets the fallback state.
 * FOR TESTING ONLY - do not use in production code.
 */
export function resetFallbackState(): void {
  fallbackActive = false;
}
